import React from "react";

import "@fontsource/lexend-deca/300.css";
import "@fontsource/lexend-deca/400.css";
import "@fontsource/lexend-deca/500.css";
import "@fontsource/lexend-deca/600.css";
import "@fontsource/lexend-deca/700.css";

import { AnimatePresence, motion } from "framer-motion";

import Lenis from "lenis";

import "lenis/dist/lenis.css";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HomeIntroAnimation from "./components/HomeIntroAnimation";
import HomeHeroAboutSection from "./sections/HomeHeroAboutSection";
import StakeholderEcosystemSection from "./sections/StakeholderEcosystemSection";
import RoleJourneySection from "./sections/RoleJourneySection";
import PartnersSection from "./sections/PartnersSection";
import LandingFooter from "../../../components/layout/LandingFooter";
import { ArrowUp } from "lucide-react";

import "./HomePage.css";

gsap.registerPlugin(ScrollTrigger);

const HOME_INTRO_SESSION_KEY = "legup_home_intro_seen";

function shouldShowHomeIntro() {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    return sessionStorage.getItem(HOME_INTRO_SESSION_KEY) !== "true";
  } catch {
    return true;
  }
}

export default function HomePage() {
  const initialShowIntro = React.useMemo(() => shouldShowHomeIntro(), []);

  const [showIntro, setShowIntro] = React.useState(initialShowIntro);

  const [contentMounted, setContentMounted] = React.useState(!initialShowIntro);

  const mainRef = React.useRef(null);
  const lenisRef = React.useRef(null);

  const [showScrollTop, setShowScrollTop] = React.useState(false);

  const handleIntroComplete = React.useCallback(() => {
    setShowIntro(false);
  }, []);

  React.useEffect(() => {
    if (!initialShowIntro) {
      return;
    }

    try {
      sessionStorage.setItem(HOME_INTRO_SESSION_KEY, "true");
    } catch {
      return;
    }
  }, [initialShowIntro]);

  React.useLayoutEffect(() => {
    const previousRestoration =
      "scrollRestoration" in window.history
        ? window.history.scrollRestoration
        : null;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    ScrollTrigger.clearScrollMemory("manual");

    const forceTop = () => {
      window.scrollTo(0, 0);

      document.documentElement.scrollTop = 0;

      document.body.scrollTop = 0;
    };

    forceTop();

    let secondFrame = null;

    const firstFrame = requestAnimationFrame(() => {
      forceTop();

      secondFrame = requestAnimationFrame(() => {
        forceTop();
      });
    });

    const handlePageShow = () => {
      forceTop();
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      cancelAnimationFrame(firstFrame);

      if (secondFrame) {
        cancelAnimationFrame(secondFrame);
      }

      window.removeEventListener("pageshow", handlePageShow);

      if (previousRestoration !== null) {
        window.history.scrollRestoration = previousRestoration;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!contentMounted || !mainRef.current) {
      return undefined;
    }

    const main = mainRef.current;

    const lenis = new Lenis({
      lerp: 0.105,

      smoothWheel: true,

      syncTouch: false,

      anchors: true,
    });

    lenisRef.current = lenis;

    let destroyed = false;

    const forceTop = () => {
      if (destroyed) {
        return;
      }

      window.scrollTo(0, 0);

      document.documentElement.scrollTop = 0;

      document.body.scrollTop = 0;

      lenis.scrollTo(0, {
        immediate: true,

        force: true,
      });
    };

    forceTop();

    const handleLenisScroll = ({ scroll }) => {
      ScrollTrigger.update();

      setShowScrollTop(scroll > 500);
    };

    lenis.on("scroll", handleLenisScroll);

    const tick = (time) => {
      lenis.raf(time * 1000);
    };

    gsap.ticker.add(tick);

    gsap.ticker.lagSmoothing(0);

    let refreshTimer = null;

    let refreshFrame = null;

    const refreshNow = () => {
      if (destroyed) {
        return;
      }

      lenis.resize();

      ScrollTrigger.refresh();

      ScrollTrigger.update();
    };

    const scheduleRefresh = (delay = 160) => {
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
      }

      if (refreshFrame) {
        cancelAnimationFrame(refreshFrame);

        refreshFrame = null;
      }

      refreshTimer = window.setTimeout(
        () => {
          refreshTimer = null;

          refreshFrame = requestAnimationFrame(() => {
            refreshFrame = null;

            refreshNow();
          });
        },

        delay,
      );
    };

    let secondInitialFrame = null;

    const firstInitialFrame = requestAnimationFrame(() => {
      secondInitialFrame = requestAnimationFrame(() => {
        forceTop();

        refreshNow();

        forceTop();
      });
    });

    const stakeholder = main.querySelector(".stakeholder-ecosystem");

    let stakeholderObserver = null;

    let lastStakeholderHeight =
      stakeholder?.getBoundingClientRect().height ?? 0;

    if (stakeholder && typeof ResizeObserver !== "undefined") {
      stakeholderObserver = new ResizeObserver(() => {
        const nextHeight = stakeholder.getBoundingClientRect().height;

        if (Math.abs(nextHeight - lastStakeholderHeight) < 1) {
          return;
        }

        lastStakeholderHeight = nextHeight;

        scheduleRefresh(260);
      });

      stakeholderObserver.observe(stakeholder);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (destroyed) {
          return;
        }

        scheduleRefresh(80);
      });
    }

    const handleLoad = () => {
      scheduleRefresh(80);
    };

    window.addEventListener("load", handleLoad);

    const handleResize = () => {
      scheduleRefresh(180);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      destroyed = true;

      cancelAnimationFrame(firstInitialFrame);

      if (secondInitialFrame) {
        cancelAnimationFrame(secondInitialFrame);
      }

      if (refreshFrame) {
        cancelAnimationFrame(refreshFrame);
      }

      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
      }

      stakeholderObserver?.disconnect();

      window.removeEventListener("load", handleLoad);

      window.removeEventListener("resize", handleResize);

      lenis.off("scroll", handleLenisScroll);

      gsap.ticker.remove(tick);

      lenisRef.current = null;

      lenis.destroy();

      gsap.ticker.lagSmoothing(500, 33);
    };
  }, [contentMounted]);

  const handleScrollToTop = React.useCallback(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, {
        duration: 1.15,
        easing: (t) => 1 - Math.pow(1 - t, 4),
      });

      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="home-page">
      <AnimatePresence
        mode="wait"
        onExitComplete={() => {
          setContentMounted(true);
        }}
      >
        {showIntro ? (
          <HomeIntroAnimation
            key="home-intro"
            onComplete={handleIntroComplete}
          />
        ) : null}
      </AnimatePresence>

      {contentMounted ? (
        <motion.div
          key="home-content"
          className="home-page__content"
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          transition={{
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <main ref={mainRef}>
            <HomeHeroAboutSection active />

            <StakeholderEcosystemSection />

            <RoleJourneySection />

            <PartnersSection />
          </main>

          <LandingFooter />
        </motion.div>
      ) : null}

      <AnimatePresence>
        {contentMounted && showScrollTop ? (
          <motion.button
            key="scroll-to-top"
            type="button"
            className="home-page__scroll-top"
            onClick={handleScrollToTop}
            aria-label="Scroll to top"
            title="Back to top"
            initial={{
              opacity: 0,
              scale: 0.82,
              y: 12,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.82,
              y: 12,
            }}
            transition={{
              duration: 0.25,
              ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{
              y: -3,
            }}
            whileTap={{
              scale: 0.94,
            }}
          >
            <ArrowUp size={20} strokeWidth={2.2} aria-hidden="true" />
          </motion.button>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
